import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatMoney, cn } from "@/lib/utils";
import { trpcClient } from "@/lib/trpc";
import { useVendorLookup } from "@/hooks/useFKLookup";
import { toast } from "sonner";
import {
  ShoppingBag,
  Building2,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { toVnDateString } from "@apv-erp/status";
interface BatchCreatePODialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: any[];
  onSuccess?: () => void;
}

interface EditableItem {
  id: string; // materialPlanId
  materialId: string;
  materialCode: string;
  materialName: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  vendorId: string | null;
  vendorName: string | null;
  notes: string;
}

export function BatchCreatePODialog({
  open,
  onOpenChange,
  selectedItems,
  onSuccess,
}: BatchCreatePODialogProps) {
  const navigate = useNavigate();
  const [items, setItems] = useState<EditableItem[]>([]);
  const [expectedDates, setExpectedDates] = useState<Record<string, string>>({});
  const [vendorNotes, setVendorNotes] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdPOs, setCreatedPOs] = useState<any[] | null>(null);

  const { options: vendorOptions, isLoading: isVendorsLoading } = useVendorLookup(open);

  // Khởi tạo state từ selectedItems khi modal mở
  useEffect(() => {
    if (open && selectedItems.length > 0) {
      const defaultDate = toVnDateString(Date.now() + 3 * 24 * 60 * 60 * 1000);

      const mapped: EditableItem[] = selectedItems.map((item) => {
        const qty = item.shortageQty > 0 ? item.shortageQty : item.neededQty || item.requiredQty || 1;
        return {
          id: item.id,
          materialId: item.plannedMaterialId || item.originalMaterialId || item.id,
          materialCode: item.materialCode || "",
          materialName: item.materialName || "Vật tư",
          unit: item.unit || "sheet",
          quantity: Math.ceil(qty),
          unitPrice: Number(item.unitPrice || 0),
          vendorId: item.vendorId || null,
          vendorName: item.vendorName || null,
          notes: "",
        };
      });

      setItems(mapped);
      setCreatedPOs(null);

      // Default dates per vendor
      const dates: Record<string, string> = {};
      mapped.forEach((i) => {
        const vKey = i.vendorId || "UNASSIGNED";
        if (!dates[vKey]) dates[vKey] = defaultDate;
      });
      setExpectedDates(dates);
    }
  }, [open, selectedItems]);

  // Phân nhóm vật tư theo Vendor
  const groupedByVendor = useMemo(() => {
    const groups = new Map<string, { vendorId: string | null; vendorName: string; items: EditableItem[] }>();

    for (const item of items) {
      const vKey = item.vendorId || "UNASSIGNED";
      if (!groups.has(vKey)) {
        groups.set(vKey, {
          vendorId: item.vendorId,
          vendorName: item.vendorName || "Chưa gán Nhà Cung Cấp",
          items: [],
        });
      }
      groups.get(vKey)!.items.push(item);
    }

    return Array.from(groups.entries()).map(([key, val]) => ({
      key,
      ...val,
      totalAmount: val.items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0),
    }));
  }, [items]);

  const handleUpdateItem = (id: string, patch: Partial<EditableItem>) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id === id) {
          const updated = { ...it, ...patch };
          // Nếu đổi vendorId, tự động cập nhật vendorName
          if (patch.vendorId !== undefined) {
            const vOpt = vendorOptions.find((o) => o.id === patch.vendorId);
            updated.vendorName = vOpt ? vOpt.label : null;
          }
          return updated;
        }
        return it;
      })
    );
  };

  const handleRemoveItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const grandTotal = useMemo(() => {
    return items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  }, [items]);

  const unassignedCount = useMemo(() => {
    return items.filter((i) => !i.vendorId).length;
  }, [items]);

  const handleSubmit = async () => {
    if (unassignedCount > 0) {
      toast.error(`Còn ${unassignedCount} vật tư chưa chọn Nhà Cung Cấp. Vui lòng chọn NCC trước khi tạo PO!`);
      return;
    }

    if (items.length === 0) {
      toast.error("Không có vật tư nào để tạo đơn mua hàng");
      return;
    }

    setIsSubmitting(true);
    try {
      const ordersPayload = groupedByVendor.map((g) => ({
        vendorId: g.vendorId!,
        expectedDate: expectedDates[g.key] || undefined,
        notes: vendorNotes[g.key] || undefined,
        items: g.items.map((i) => ({
          materialPlanId: i.id,
          materialId: i.materialId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          notes: i.notes || undefined,
        })),
      }));

      const res = await trpcClient.materialPlanning.createPurchaseOrdersFromPlans.mutate({
        planIds: items.map((i) => i.id),
        orders: ordersPayload,
      });

      toast.success(res.message || `Đã tạo thành công ${res.createdPOs?.length || 0} đơn mua hàng!`);
      setCreatedPOs(res.createdPOs || []);
      onSuccess?.();
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || "Lỗi khi tạo đơn mua hàng");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2 text-indigo-700">
            <ShoppingBag className="w-5 h-5" />
            <DialogTitle className="text-base font-bold">
              Tạo Đơn Mua Hàng Tự Động (MRP Purchase Orders)
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-slate-500">
            Hệ thống tự động gom nhóm {items.length} vật tư đã chọn theo từng Nhà Cung Cấp để sinh các đơn PO riêng biệt.
          </DialogDescription>
        </DialogHeader>

        {/* Nội dung danh sách đơn gom */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {createdPOs ? (
            <div className="py-6 flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800">
                  Tạo Thành Công {createdPOs.length} Đơn Mua Hàng!
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Các đơn mua hàng đã được lưu và cập nhật trạng thái kế hoạch vật tư sang ORDERED.
                </p>
              </div>

              <div className="w-full max-w-lg space-y-2 text-left">
                {createdPOs.map((po: any) => (
                  <div
                    key={po.id}
                    className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-sm hover:border-indigo-400 transition-colors"
                  >
                    <div>
                      <div className="font-mono font-bold text-sm text-indigo-600">
                        {po.poId}
                      </div>
                      <div className="text-xs text-slate-600 mt-0.5">
                        {po.vendorName} ({po.itemsCount} mặt hàng)
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-bold text-slate-800">
                        {formatMoney(po.totalAmount)}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        onClick={() => {
                          onOpenChange(false);
                          navigate({
                            to: "/purchasing/$poId",
                            params: { poId: po.id || po.poId },
                          });
                        }}
                      >
                        Xem PO <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {unassignedCount > 0 && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>
                    Có <strong>{unassignedCount}</strong> vật tư chưa có Nhà Cung Cấp mặc định. Vui lòng chọn NCC bên dưới trước khi tạo đơn.
                  </span>
                </div>
              )}

              {groupedByVendor.map((group) => {
                const isUnassigned = !group.vendorId;
                return (
                  <Card
                    key={group.key}
                    className={cn(
                      "overflow-hidden border",
                      isUnassigned ? "border-amber-300 bg-amber-50/20" : "border-slate-200"
                    )}
                  >
                    <CardHeader className="py-2.5 px-4 bg-slate-50/70 border-b border-slate-100 flex flex-row items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        {isUnassigned ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-amber-700">Chưa gán NCC:</span>
                            <span className="text-xs text-slate-500">
                              (Chọn NCC cho từng vật tư trong bảng)
                            </span>
                          </div>
                        ) : (
                          <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <span>{group.vendorName}</span>
                            <Badge variant="outline" className="text-[10px] font-normal bg-white">
                              {group.items.length} mặt hàng
                            </Badge>
                          </CardTitle>
                        )}
                      </div>

                      {!isUnassigned && (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1.5 text-xs text-slate-600">
                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                            <span>Ngày giao:</span>
                            <Input
                              type="date"
                              className="h-7 text-xs w-32 bg-white"
                              value={expectedDates[group.key] || ""}
                              onChange={(e) =>
                                setExpectedDates((prev) => ({
                                  ...prev,
                                  [group.key]: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] text-slate-400 block">Tổng tiền PO</span>
                            <span className="font-mono text-xs font-bold text-indigo-600">
                              {formatMoney(group.totalAmount)}
                            </span>
                          </div>
                        </div>
                      )}
                    </CardHeader>

                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="h-8 text-[11px] bg-white">
                            <TableHead className="w-[120px]">Mã VT</TableHead>
                            <TableHead className="min-w-[180px]">Tên Vật Tư</TableHead>
                            {isUnassigned && (
                              <TableHead className="w-[180px]">Gán NCC</TableHead>
                            )}
                            <TableHead className="w-[70px] text-center">ĐVT</TableHead>
                            <TableHead className="w-[110px] text-right">SL Mua</TableHead>
                            <TableHead className="w-[120px] text-right">Đơn Giá (đ)</TableHead>
                            <TableHead className="w-[120px] text-right">Thành Tiền</TableHead>
                            <TableHead className="w-[40px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.items.map((item) => (
                            <TableRow key={item.id} className="h-9 text-xs">
                              <TableCell className="font-mono text-[11px] text-slate-600 font-semibold py-1">
                                {item.materialCode || "—"}
                              </TableCell>
                              <TableCell className="font-medium text-slate-900 py-1">
                                {item.materialName}
                              </TableCell>
                              {isUnassigned && (
                                <TableCell className="py-1">
                                  <Select
                                    value={item.vendorId || ""}
                                    onValueChange={(val) => handleUpdateItem(item.id, { vendorId: val })}
                                  >
                                    <SelectTrigger className="h-7 text-xs bg-white">
                                      <SelectValue placeholder="Chọn Nhà Cung Cấp..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {vendorOptions.map((opt) => (
                                        <SelectItem key={opt.id} value={opt.id}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                              )}
                              <TableCell className="text-center text-slate-500 py-1">
                                {item.unit}
                              </TableCell>
                              <TableCell className="text-right py-1">
                                <Input
                                  type="number"
                                  className="h-7 text-xs text-right font-mono font-bold w-24 ml-auto"
                                  min={1}
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleUpdateItem(item.id, {
                                      quantity: Math.max(1, Number(e.target.value)),
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell className="text-right py-1">
                                <Input
                                  type="number"
                                  className="h-7 text-xs text-right font-mono w-24 ml-auto"
                                  min={0}
                                  value={item.unitPrice}
                                  onChange={(e) =>
                                    handleUpdateItem(item.id, {
                                      unitPrice: Math.max(0, Number(e.target.value)),
                                    })
                                  }
                                />
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-slate-700 py-1">
                                {formatMoney(item.quantity * item.unitPrice)}
                              </TableCell>
                              <TableCell className="py-1 text-center">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-slate-400 hover:text-rose-600"
                                  onClick={() => handleRemoveItem(item.id)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </div>

        {/* Footer tổng kết và hành động */}
        <DialogFooter className="p-3 px-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between sm:justify-between">
          {!createdPOs ? (
            <>
              <div className="flex items-center gap-4 text-xs text-slate-600">
                <div>
                  Tổng số PO:{" "}
                  <span className="font-bold text-slate-900">
                    {groupedByVendor.filter((g) => !!g.vendorId).length}
                  </span>
                </div>
                <div>
                  Tổng mặt hàng:{" "}
                  <span className="font-bold text-slate-900">{items.length}</span>
                </div>
                <div>
                  Tổng giá trị dự kiến:{" "}
                  <span className="font-mono text-sm font-bold text-indigo-600">
                    {formatMoney(grandTotal)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Hủy
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                  onClick={handleSubmit}
                  disabled={isSubmitting || items.length === 0}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      Đang tạo đơn...
                    </>
                  ) : (
                    <>
                      <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                      Xác Nhận Tạo Đơn Mua Hàng
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-end w-full">
              <Button
                type="button"
                size="sm"
                className="h-8 text-xs bg-slate-900 text-white"
                onClick={() => onOpenChange(false)}
              >
                Đóng
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
